import { useState } from "react";
import { useTranslation } from "react-i18next";
import { postTenantComment, postTenantSolved, type TenantCaseResponse } from "../api/portal";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelative(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `vor ${diffSec} Sek.`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `vor ${diffMin} Min.`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `vor ${diffH} Std.`;
  const diffD = Math.floor(diffH / 24);
  return `vor ${diffD} Tag${diffD === 1 ? "" : "en"}`;
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

function StatusBadge({ status, label }: { status: string; label: string }) {
  const colorMap: Record<string, string> = {
    open: "bg-blue-100 text-blue-700",
    in_progress: "bg-yellow-100 text-yellow-700",
    resolved: "bg-green-100 text-green-700",
  };
  const color = colorMap[status] ?? "bg-gray-100 text-gray-600";
  return (
    <span className={`inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full ${color}`}>
      {label}
    </span>
  );
}

function PriorityBadge({ priority, label }: { priority: string; label: string }) {
  const colorMap: Record<string, string> = {
    low: "bg-gray-100 text-gray-600",
    medium: "bg-blue-100 text-blue-700",
    high: "bg-orange-100 text-orange-700",
    critical: "bg-red-100 text-red-700",
  };
  const color = colorMap[priority] ?? "bg-gray-100 text-gray-600";
  return (
    <span className={`inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full ${color}`}>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TicketViewPageProps {
  token: string;
  caseData: TenantCaseResponse;
  lang: "de" | "en";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TicketViewPage({ token, caseData, lang }: TicketViewPageProps) {
  const { t } = useTranslation();

  const [events, setEvents] = useState(caseData.events);
  const [contactName, setContactName] = useState(caseData.contact_name);

  // Comment form state
  const [comment, setComment] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [commentSending, setCommentSending] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [commentSuccess, setCommentSuccess] = useState(false);

  // Solved form state
  const [showSolvedForm, setShowSolvedForm] = useState(false);
  const [solvedComment, setSolvedComment] = useState("");
  const [solvedSending, setSolvedSending] = useState(false);
  const [solvedError, setSolvedError] = useState<string | null>(null);
  const [solvedSuccess, setSolvedSuccess] = useState(false);

  const needsName = !contactName;

  const statusLabelMap: Record<string, string> = {
    open: t("ticket_status_open"),
    in_progress: t("ticket_status_in_progress"),
    resolved: t("ticket_status_resolved"),
  };

  const priorityLabelMap: Record<string, string> = {
    low: t("ticket_priority_low"),
    medium: t("ticket_priority_medium"),
    high: t("ticket_priority_high"),
    critical: t("ticket_priority_critical"),
  };

  // Use lang prop to adjust relative time wording if needed (currently DE wording hardcoded;
  // the lang prop is available for future i18n expansion of the helper).
  void lang;

  async function handleCommentSubmit(e: React.FormEvent) {
    e.preventDefault();
    setCommentError(null);

    if (needsName && (!firstName.trim() || !lastName.trim())) {
      setCommentError(t("ticket_name_required"));
      return;
    }
    if (!comment.trim()) return;

    setCommentSending(true);
    try {
      await postTenantComment(
        token,
        comment.trim(),
        needsName ? firstName.trim() : undefined,
        needsName ? lastName.trim() : undefined,
      );
      // Optimistically append the new event to the timeline
      const newEvent = {
        id: crypto.randomUUID(),
        event_type: "tenant_comment",
        actor: needsName ? `${firstName.trim()} ${lastName.trim()}` : (contactName ?? ""),
        comment: comment.trim(),
        created_at: new Date().toISOString(),
      };
      if (needsName) {
        setContactName(`${firstName.trim()} ${lastName.trim()}`);
      }
      setEvents((prev) => [...prev, newEvent]);
      setComment("");
      setCommentSuccess(true);
      setTimeout(() => setCommentSuccess(false), 3000);
    } catch (err) {
      setCommentError(err instanceof Error ? err.message : "comment_error");
    } finally {
      setCommentSending(false);
    }
  }

  async function handleSolvedSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSolvedError(null);
    if (solvedComment.trim().length < 10) return;

    setSolvedSending(true);
    try {
      await postTenantSolved(token, solvedComment.trim());
      setSolvedSuccess(true);
      setShowSolvedForm(false);
    } catch (err) {
      setSolvedError(err instanceof Error ? err.message : "solved_error");
    } finally {
      setSolvedSending(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-md mx-auto px-4 py-8">

        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4 shadow-sm">
          <p className="text-xs text-gray-400 mb-1">{t("ticket_number")}</p>
          <p className="text-2xl font-mono font-bold text-gray-800 mb-3">
            {caseData.ticket_number}
          </p>
          <div className="flex flex-wrap gap-2">
            <StatusBadge
              status={caseData.status}
              label={statusLabelMap[caseData.status] ?? caseData.status}
            />
            <PriorityBadge
              priority={caseData.priority}
              label={priorityLabelMap[caseData.priority] ?? caseData.priority}
            />
          </div>
        </div>

        {/* Diagnosis summary */}
        {caseData.diagnosis_summary && (
          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4 shadow-sm">
            <p className="text-xs text-gray-400 mb-1">{t("ticket_diagnosis")}</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {caseData.diagnosis_summary}
            </p>
          </div>
        )}

        {/* Timeline */}
        <div className="mb-6">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            {t("ticket_timeline")}
          </p>
          <div className="flex flex-col gap-3">
            {events.map((ev) => {
              const isTenant = ev.event_type === "tenant_comment";
              return (
                <div
                  key={ev.id}
                  className={`flex flex-col max-w-[85%] ${isTenant ? "self-end items-end" : "self-start items-start"}`}
                >
                  <div
                    className={`rounded-xl px-4 py-2.5 text-sm ${
                      isTenant
                        ? "bg-gray-200 text-gray-800"
                        : "bg-blue-600 text-white"
                    }`}
                  >
                    {ev.comment && <p className="whitespace-pre-wrap">{ev.comment}</p>}
                    {!ev.comment && (
                      <p className="italic opacity-70">{ev.event_type.replace(/_/g, " ")}</p>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1 px-1">
                    {isTenant ? ev.actor : "Support-Team"} · {formatRelative(ev.created_at)}
                  </p>
                </div>
              );
            })}
            {events.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">–</p>
            )}
          </div>
        </div>

        {/* Solved success banner */}
        {solvedSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4 text-center">
            <p className="text-green-700 font-semibold text-sm">{t("ticket_solved_success")}</p>
          </div>
        )}

        {/* Comment form – only show when not solved */}
        {!solvedSuccess && (
          <form
            onSubmit={handleCommentSubmit}
            noValidate
            className="bg-white border border-gray-200 rounded-xl p-5 mb-4 shadow-sm"
          >
            {/* Name fields (only if contact_name is empty) */}
            {needsName && (
              <div className="flex gap-3 mb-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t("ticket_first_name")} *
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t("ticket_first_name")}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t("ticket_last_name")} *
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={t("ticket_last_name")}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("ticket_comment_placeholder")}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none mb-3"
            />

            {commentError && (
              <p role="alert" className="text-red-600 text-xs mb-2">
                {commentError}
              </p>
            )}
            {commentSuccess && (
              <p className="text-green-600 text-xs mb-2">{t("ticket_comment_sent")}</p>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={commentSending || !comment.trim()}
                className="py-2 px-5 btn-primary rounded-xl font-medium text-sm transition-colors disabled:cursor-not-allowed"
              >
                {commentSending ? "…" : t("ticket_comment_send")}
              </button>
            </div>
          </form>
        )}

        {/* Solved button / inline form */}
        {!solvedSuccess && !showSolvedForm && (
          <button
            type="button"
            onClick={() => setShowSolvedForm(true)}
            className="w-full py-3 px-4 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors"
          >
            {t("ticket_solved_btn")}
          </button>
        )}

        {!solvedSuccess && showSolvedForm && (
          <form
            onSubmit={handleSolvedSubmit}
            noValidate
            className="bg-white border border-green-200 rounded-xl p-5 shadow-sm"
          >
            <textarea
              value={solvedComment}
              onChange={(e) => setSolvedComment(e.target.value)}
              placeholder={t("ticket_solved_comment_placeholder")}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none mb-3"
            />
            {solvedError && (
              <p role="alert" className="text-red-600 text-xs mb-2">
                {solvedError}
              </p>
            )}
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={solvedSending || solvedComment.trim().length < 10}
                className="flex-1 py-2 px-4 bg-green-600 text-white rounded-xl font-medium text-sm hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {solvedSending ? "…" : t("ticket_solved_confirm")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowSolvedForm(false);
                  setSolvedComment("");
                  setSolvedError(null);
                }}
                className="flex-1 py-2 px-4 bg-white border border-gray-300 text-gray-700 rounded-xl font-medium text-sm hover:bg-gray-50 transition-colors"
              >
                {t("ticket_solved_cancel")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
